import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, X, ChevronDown, ChevronRight } from "lucide-react";
import RichEditor from "@/components/admin/RichEditor";
import FileUploader from "@/components/admin/FileUploader";
import BranchBuilder from "@/components/admin/BranchBuilder";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import WorkspacesTab from "@/components/admin/WorkspacesTab";
import TeamProgressTab from "@/components/admin/TeamProgressTab";
import { CallReviewsTab } from "@/components/admin/CallReviewsTab";
import DocumentsTab from "@/components/admin/DocumentsTab";
import AnalyticsTab from "@/components/admin/AnalyticsTab";
import EmailsTab from "@/components/admin/EmailsTab";
import AiModelsTab from "@/components/admin/AiModelsTab";
import BotAnalyticsTab from "@/components/admin/BotAnalyticsTab";
import ScorecardTab from "@/components/admin/ScorecardTab";
import CommissionsTab from "@/components/admin/CommissionsTab";
import TracksTab from "@/components/admin/TracksTab";
import { UserForm } from "@/components/admin/UserForm";
import { useTracks } from "@/context/TrackContext";

const STAGES = ["methodology", "market", "product", "admin", "metrics"];
const CATEGORIES = ["script", "template", "pdf", "video", "tool"];
const DIFFICULTIES = ["beginner", "intermediate", "advanced"];
const KINDS = ["text", "video", "script", "pdf"];

const input = "w-full bg-transparent border hairline px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]";

export default function Admin() {
  const { user } = useAuth();
  const { workspace, reload: reloadWs } = useWorkspace();
  const isSuper = user?.role === "super_admin";
  const [tab, setTab] = useState("modules");
  const [modules, setModules] = useState([]);
  const [resources, setResources] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [users, setUsers] = useState([]);

  const reload = () => {
    api.get("/modules").then(r => setModules(r.data));
    api.get("/resources").then(r => setResources(r.data));
    api.get("/scenarios").then(r => setScenarios(r.data));
    api.get("/admin/users").then(r => setUsers(r.data));
  };
  useEffect(reload, []);

  // Super-admin: switch the workspace whose content is being managed
  const switchWorkspace = async (slug) => {
    localStorage.setItem("wp_ws_slug", slug);
    await reloadWs();
    reload();
    toast.success(`Managing workspace: ${slug}`);
  };

  const tabBtn = (k, label) => (
    <button
      onClick={()=>setTab(k)}
      data-testid={`admin-tab-${k}`}
      className={`px-4 py-2 text-sm border-b transition-colors ${tab===k?"border-[color:var(--brand-primary)] text-[var(--brand-primary)]":"border-transparent text-neutral-500 hover:text-black"}`}
    >{label}</button>
  );

  return (
    <div className="space-y-10">
      <div>
        <div className="label-eyebrow mb-3">Control Room</div>
        <h1 className="font-display text-5xl md:text-6xl leading-[1.02]">Admin</h1>
        <p className="text-neutral-500 mt-3">Inline edit · rich text · file uploads · visual branching builder.</p>
        {isSuper && (
          <div className="mt-4 inline-flex items-center gap-2 border hairline bg-white px-4 py-2 text-sm" data-testid="active-workspace-banner">
            <span className="label-eyebrow" style={{fontSize:'0.55rem'}}>Managing workspace</span>
            <span className="font-display text-base">{workspace?.name || "—"}</span>
            <span className="font-mono text-xs text-neutral-400">/{workspace?.slug}</span>
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b hairline flex-wrap">
        {tabBtn("modules","Modules")}
        {tabBtn("tracks","Tracks")}
        {tabBtn("resources","Resources")}
        {tabBtn("scenarios","Scenarios")}
        {tabBtn("documents","Documents")}
        {tabBtn("users","Users")}
        {tabBtn("team","Team Progress")}
        {tabBtn("scorecard","Scorecard")}
        {tabBtn("botanalytics","Bot Analytics")}
        {tabBtn("commissions","Commissions")}
        {tabBtn("callreviews","Call Reviews")}
        {tabBtn("emails","Emails")}
        {tabBtn("aimodels","AI Models")}
        {isSuper && tabBtn("workspaces","Workspaces")}
        {isSuper && tabBtn("analytics","Analytics")}
      </div>

      {tab === "modules"   && <ModulesTab modules={modules} reload={reload}/>}
      {tab === "tracks"    && <TracksTab/>}
      {tab === "resources" && <ResourcesTab resources={resources} reload={reload}/>}
      {tab === "scenarios" && <ScenariosTab scenarios={scenarios} reload={reload}/>}
      {tab === "documents" && <DocumentsTab/>}
      {tab === "users"     && <UsersTab users={users} reload={reload}/>}
      {tab === "team"      && <TeamProgressTab/>}
      {tab === "scorecard" && <ScorecardTab/>}
      {tab === "botanalytics" && <BotAnalyticsTab/>}
      {tab === "commissions" && <CommissionsTab/>}
      {tab === "callreviews" && <CallReviewsTab/>}
      {tab === "emails" && <EmailsTab/>}
      {tab === "aimodels" && <AiModelsTab/>}
      {tab === "workspaces" && isSuper && <WorkspacesTab activeSlug={workspace?.slug} onSwitch={switchWorkspace}/>}
      {tab === "analytics" && isSuper && <AnalyticsTab/>}
    </div>
  );
}

// -------------------------------------------------------------------- MODULES
function ModulesTab({ modules, reload }) {
  const { tracks } = useTracks();
  const trackName = (id) => tracks.find((t) => t.id === id)?.name || null;
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const blankLesson = () => ({ title: "", body: "", kind: "text", media_url: "", duration_min: 5 });
  const blankModule = () => ({ stage: "methodology", title: "", summary: "", order: 0, icon: "book-open", track_id: null, lessons: [] });

  return (
    <div className="space-y-6">
      <button onClick={()=>{setCreating(true); setEditingId(null);}} className="btn-primary flex items-center gap-2" data-testid="mod-new">
        <Plus size={14}/> New module
      </button>

      {creating && (
        <ModuleForm initial={blankModule()} onCancel={()=>setCreating(false)}
          onSave={async (data) => { await api.post("/modules", data); toast.success("Module created"); setCreating(false); reload(); }}
        />
      )}

      <div className="space-y-3">
        {modules.map(m => (
          <div key={m.id} className="border hairline bg-white" data-testid={`admin-mod-${m.id}`}>
            {editingId === m.id ? (
              <div className="p-4">
                <ModuleForm initial={m} onCancel={()=>setEditingId(null)}
                  onSave={async (data) => { await api.patch(`/modules/${m.id}`, data); toast.success("Saved"); setEditingId(null); reload(); }}
                />
              </div>
            ) : (
              <>
                <div className="p-4 flex items-center justify-between">
                  <button className="flex items-center gap-3 text-left flex-1" onClick={()=>setExpanded(e=>({...e, [m.id]:!e[m.id]}))}>
                    {expanded[m.id] ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                    <div>
                      <div className="text-xs font-mono text-neutral-400">
                        {m.stage} · order {m.order}
                        {trackName(m.track_id) && <span className="ml-2 px-1.5 py-0.5 border hairline text-[10px] uppercase tracking-wider text-[var(--brand-primary)]">{trackName(m.track_id)}</span>}
                      </div>
                      <div className="font-medium">{m.title}</div>
                      <div className="text-xs text-neutral-500">{(m.lessons||[]).length} lessons</div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2">
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={()=>setEditingId(m.id)} data-testid={`edit-mod-${m.id}`}>
                      <Pencil size={12}/> Edit
                    </button>
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={async ()=>{
                      if (!window.confirm(`Delete "${m.title}"?`)) return;
                      await api.delete(`/modules/${m.id}`); toast.success("Deleted"); reload();
                    }} data-testid={`del-mod-${m.id}`}><Trash2 size={12}/> Delete</button>
                  </div>
                </div>
                {expanded[m.id] && (
                  <div className="border-t hairline p-4 space-y-2 bg-[color:var(--brand-surface2)]/30">
                    {(m.lessons||[]).map((l,i)=>(
                      <div key={i} className="text-sm p-2 border hairline bg-white">
                        <div className="label-eyebrow" style={{fontSize:'0.55rem'}}>{l.kind} · {l.duration_min}m</div>
                        <div className="font-medium">{l.title}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Helper for blank lesson used by ModuleForm */}
      <div className="hidden">{JSON.stringify(blankLesson())}</div>
    </div>
  );
}

function ModuleForm({ initial, onSave, onCancel }) {
  const { tracks } = useTracks();
  const [f, setF] = useState(initial);
  const upd = (patch) => setF({...f, ...patch});
  const updLesson = (i, patch) => setF({...f, lessons: f.lessons.map((l,idx)=>idx===i?{...l,...patch}:l)});
  const addLesson = () => setF({...f, lessons: [...(f.lessons||[]), { title: "", body: "", kind: "text", media_url: "", duration_min: 5 }]});
  const rmLesson = (i) => setF({...f, lessons: f.lessons.filter((_,idx)=>idx!==i)});

  return (
    <div className="border hairline bg-white p-5 space-y-4">
      <div className="grid md:grid-cols-4 gap-3">
        <select className={input} value={f.stage} onChange={e=>upd({stage:e.target.value})} data-testid="mod-stage">
          {STAGES.map(s=><option key={s}>{s}</option>)}
        </select>
        <select className={input} value={f.track_id || ""} onChange={e=>upd({track_id: e.target.value || null})} data-testid="mod-track">
          <option value="">Shared (all tracks)</option>
          {tracks.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <input className={input} placeholder="Title" value={f.title} onChange={e=>upd({title:e.target.value})} data-testid="mod-title"/>
        <input type="number" className={input} placeholder="Order" value={f.order} onChange={e=>upd({order:Number(e.target.value)})} data-testid="mod-order"/>
      </div>
      <textarea rows={2} className={input} placeholder="Summary" value={f.summary} onChange={e=>upd({summary:e.target.value})} data-testid="mod-summary"/>

      <div>
        <div className="label-eyebrow mb-2">Lessons</div>
        <div className="space-y-3">
          {(f.lessons||[]).map((l,i)=>(
            <div key={i} className="border hairline p-3 bg-[color:var(--brand-surface2)]/40 space-y-2">
              <div className="grid md:grid-cols-12 gap-2">
                <input className={`${input} md:col-span-6`} placeholder="Lesson title" value={l.title} onChange={e=>updLesson(i,{title:e.target.value})} data-testid={`mod-lesson-title-${i}`}/>
                <select className={`${input} md:col-span-2`} value={l.kind} onChange={e=>updLesson(i,{kind:e.target.value})} data-testid={`mod-lesson-kind-${i}`}>{KINDS.map(k=><option key={k}>{k}</option>)}</select>
                <input type="number" className={`${input} md:col-span-2`} placeholder="min" value={l.duration_min} onChange={e=>updLesson(i,{duration_min:Number(e.target.value)})}/>
                <button type="button" onClick={()=>rmLesson(i)} className="md:col-span-2 btn-ghost text-xs flex items-center justify-center gap-1"><Trash2 size={12}/> Remove</button>
              </div>
              <div className="grid md:grid-cols-12 gap-2 items-start">
                <input className={`${input} md:col-span-8`} placeholder="Media URL (video/pdf)" value={l.media_url} onChange={e=>updLesson(i,{media_url:e.target.value})} data-testid={`mod-lesson-media-${i}`}/>
                <div className="md:col-span-4">
                  <FileUploader
                    onUploaded={(url)=>updLesson(i,{media_url:url})}
                    label="Upload media"
                    testid={`mod-upload-${i}`}
                  />
                </div>
              </div>
              <RichEditor value={l.body || ""} onChange={(v)=>updLesson(i,{body:v})} testid={`mod-lesson-body-${i}`} minRows={4}/>
            </div>
          ))}
          <button type="button" onClick={addLesson} className="btn-ghost text-xs flex items-center gap-1" data-testid="mod-add-lesson"><Plus size={12}/> Add lesson</button>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button className="btn-primary flex items-center gap-2" onClick={()=>onSave(f)} data-testid="mod-save"><Save size={14}/> Save</button>
        <button className="btn-ghost flex items-center gap-2" onClick={onCancel} data-testid="mod-cancel"><X size={14}/> Cancel</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ RESOURCES
function ResourcesTab({ resources, reload }) {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blank = () => ({ title: "", description: "", category: "script", stage: "methodology", body: "", url: "", tags: [] });

  return (
    <div className="space-y-6">
      <button onClick={()=>{setCreating(true); setEditingId(null);}} className="btn-primary flex items-center gap-2" data-testid="res-new">
        <Plus size={14}/> New resource
      </button>

      {creating && <ResourceForm initial={blank()} onCancel={()=>setCreating(false)}
        onSave={async(d)=>{ await api.post("/resources", d); toast.success("Created"); setCreating(false); reload(); }}/>}

      <div className="space-y-3">
        {resources.map(r => (
          <div key={r.id} className="border hairline bg-white" data-testid={`admin-res-${r.id}`}>
            {editingId === r.id
              ? <div className="p-4"><ResourceForm initial={r} onCancel={()=>setEditingId(null)}
                  onSave={async(d)=>{ await api.patch(`/resources/${r.id}`, d); toast.success("Saved"); setEditingId(null); reload(); }}/></div>
              : (
                <div className="p-4 flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-xs font-mono text-neutral-400">{r.category} · {r.stage || "—"}</div>
                    <div className="font-medium">{r.title}</div>
                    <div className="text-xs text-neutral-500">{r.description}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={()=>setEditingId(r.id)} data-testid={`edit-res-${r.id}`}><Pencil size={12}/> Edit</button>
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={async()=>{
                      if (!window.confirm(`Delete "${r.title}"?`)) return;
                      await api.delete(`/resources/${r.id}`); toast.success("Deleted"); reload();
                    }} data-testid={`del-res-${r.id}`}><Trash2 size={12}/> Delete</button>
                  </div>
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResourceForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({...initial, tags: (initial.tags||[]).join(", ")});
  const upd = (p) => setF({...f, ...p});
  const submit = () => onSave({...f, tags: f.tags.split(",").map(t=>t.trim()).filter(Boolean)});

  return (
    <div className="border hairline bg-white p-5 space-y-3">
      <div className="grid md:grid-cols-3 gap-3">
        <input className={input} placeholder="Title" value={f.title} onChange={e=>upd({title:e.target.value})} data-testid="res-title"/>
        <select className={input} value={f.category} onChange={e=>upd({category:e.target.value})} data-testid="res-cat">
          {CATEGORIES.map(s=><option key={s}>{s}</option>)}
        </select>
        <select className={input} value={f.stage} onChange={e=>upd({stage:e.target.value})} data-testid="res-stage">
          {STAGES.map(s=><option key={s}>{s}</option>)}
        </select>
      </div>
      <input className={input} placeholder="Description" value={f.description} onChange={e=>upd({description:e.target.value})} data-testid="res-desc"/>
      <div className="grid md:grid-cols-12 gap-2 items-start">
        <input className={`${input} md:col-span-8`} placeholder="External URL (optional)" value={f.url} onChange={e=>upd({url:e.target.value})}/>
        <div className="md:col-span-4">
          <FileUploader onUploaded={(url)=>upd({url})} testid="res-upload"/>
        </div>
      </div>
      <RichEditor value={f.body || ""} onChange={(v)=>upd({body:v})} testid="res-body"/>
      <input className={input} placeholder="tags (comma-separated)" value={f.tags} onChange={e=>upd({tags:e.target.value})} data-testid="res-tags"/>
      <div className="flex items-center gap-2">
        <button className="btn-primary flex items-center gap-2" onClick={submit} data-testid="res-save"><Save size={14}/> Save</button>
        <button className="btn-ghost flex items-center gap-2" onClick={onCancel}><X size={14}/> Cancel</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ SCENARIOS
function ScenariosTab({ scenarios, reload }) {
  const { tracks } = useTracks();
  const trackName = (id) => tracks.find((t) => t.id === id)?.name || null;
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blank = () => ({ stage: "methodology", title: "", description: "", kind: "ai_chat", persona: "", difficulty: "beginner", track_id: null, branches: [] });

  return (
    <div className="space-y-6">
      <button onClick={()=>{setCreating(true); setEditingId(null);}} className="btn-primary flex items-center gap-2" data-testid="sc-new">
        <Plus size={14}/> New scenario
      </button>

      {creating && <ScenarioForm initial={blank()} onCancel={()=>setCreating(false)}
        onSave={async(d)=>{ await api.post("/scenarios", d); toast.success("Created"); setCreating(false); reload(); }}/>}

      <div className="space-y-3">
        {scenarios.map(s => (
          <div key={s.id} className="border hairline bg-white" data-testid={`admin-sc-${s.id}`}>
            {editingId === s.id
              ? <div className="p-4"><ScenarioForm initial={s} onCancel={()=>setEditingId(null)}
                  onSave={async(d)=>{ await api.patch(`/scenarios/${s.id}`, d); toast.success("Saved"); setEditingId(null); reload(); }}/></div>
              : (
                <div className="p-4 flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-xs font-mono text-neutral-400">
                      {s.stage} · {s.kind} · {s.difficulty}
                      {trackName(s.track_id) && <span className="ml-2 px-1.5 py-0.5 border hairline text-[10px] uppercase tracking-wider text-[var(--brand-primary)]">{trackName(s.track_id)}</span>}
                    </div>
                    <div className="font-medium">{s.title}</div>
                    <div className="text-xs text-neutral-500">{s.description}</div>
                    {s.kind === "branching" && <div className="text-[10px] font-mono text-neutral-400 mt-1">{(s.branches||[]).length} nodes</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={()=>setEditingId(s.id)} data-testid={`edit-sc-${s.id}`}><Pencil size={12}/> Edit</button>
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={async()=>{
                      if (!window.confirm(`Delete "${s.title}"?`)) return;
                      await api.delete(`/scenarios/${s.id}`); toast.success("Deleted"); reload();
                    }} data-testid={`del-sc-${s.id}`}><Trash2 size={12}/> Delete</button>
                  </div>
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScenarioForm({ initial, onSave, onCancel }) {
  const { tracks } = useTracks();
  const [f, setF] = useState(initial);
  const upd = (p) => setF({...f, ...p});

  return (
    <div className="border hairline bg-white p-5 space-y-3">
      <div className="grid md:grid-cols-5 gap-3">
        <select className={input} value={f.stage} onChange={e=>upd({stage:e.target.value})} data-testid="sc-stage">
          {STAGES.map(s=><option key={s}>{s}</option>)}
        </select>
        <select className={input} value={f.track_id || ""} onChange={e=>upd({track_id: e.target.value || null})} data-testid="sc-track">
          <option value="">Shared (all tracks)</option>
          {tracks.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select className={input} value={f.kind} onChange={e=>upd({kind:e.target.value})} data-testid="sc-kind">
          <option value="ai_chat">AI Chat</option>
          <option value="branching">Branching</option>
        </select>
        <select className={input} value={f.difficulty} onChange={e=>upd({difficulty:e.target.value})} data-testid="sc-diff">
          {DIFFICULTIES.map(s=><option key={s}>{s}</option>)}
        </select>
        <input className={input} placeholder="Title" value={f.title} onChange={e=>upd({title:e.target.value})} data-testid="sc-title"/>
      </div>
      <textarea rows={2} className={input} placeholder="Description shown to trainee" value={f.description} onChange={e=>upd({description:e.target.value})} data-testid="sc-desc"/>

      {f.kind === "ai_chat" ? (
        <div>
          <div className="label-eyebrow mb-2">Persona (character description for the AI to role-play)</div>
          <RichEditor value={f.persona || ""} onChange={(v)=>upd({persona:v})} testid="sc-persona" minRows={6}/>
        </div>
      ) : (
        <BranchBuilder value={f.branches || []} onChange={(v)=>upd({branches:v})}/>
      )}

      <div className="flex items-center gap-2 pt-2">
        <button className="btn-primary flex items-center gap-2" onClick={()=>onSave(f)} data-testid="sc-save"><Save size={14}/> Save</button>
        <button className="btn-ghost flex items-center gap-2" onClick={onCancel}><X size={14}/> Cancel</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------- USERS
const STATUS_STYLES = {
  active: "border border-[color:var(--brand-primary)] text-[var(--brand-primary)]",
  inactive: "border border-[#a05a00] text-[#a05a00]",
  cancelled: "bg-[#8B2E2E] text-white",
};

function UsersTab({ users, reload }) {
  const { user: me } = useAuth();
  const [resets, setResets] = useState([]);
  const [tempResult, setTempResult] = useState(null);
  const [formUser, setFormUser] = useState(undefined); // undefined=closed, null=create, object=edit

  const loadResets = () => api.get("/admin/password-resets").then(r => setResets(r.data)).catch(() => {});
  useEffect(() => { loadResets(); }, []);

  const setStatus = async (u, status) => {
    if (status === "cancelled" && !window.confirm(`Cancel ${u.name}'s account? They will no longer be able to sign in.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/status`, { status });
      toast.success(`${u.name} is now ${status}`);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const del = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email}) and all their progress? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const toggleMute = async (u) => {
    const muted = !u.email_muted;
    try {
      await api.post(`/admin/users/${u.id}/mute-emails`, { muted });
      toast.success(muted ? `Emails to ${u.name} muted` : `Emails to ${u.name} unmuted`);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const approve = async (r) => {
    try {
      const { data } = await api.post(`/admin/password-resets/${r.id}/approve`);
      setTempResult(data);
      loadResets();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const deny = async (r) => {
    try {
      await api.post(`/admin/password-resets/${r.id}/deny`);
      toast.success("Request denied");
      loadResets();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const canManage = (u) => u.id !== me?.id && (me?.role === "super_admin" || u.role === "user");
  const btn = "btn-ghost text-xs px-2 py-1";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm text-neutral-500">{users.length} account{users.length !== 1 ? "s" : ""} in this workspace</div>
        <button className="btn-primary text-sm" onClick={() => setFormUser(null)} data-testid="add-user-btn">+ Add user</button>
      </div>

      {formUser !== undefined && (
        <UserForm
          initial={formUser}
          onClose={() => setFormUser(undefined)}
          onSaved={() => { setFormUser(undefined); reload(); }}
        />
      )}

      {resets.length > 0 && (
        <div className="border-2 border-[color:var(--brand-primary)] bg-white p-5" data-testid="reset-requests-panel">
          <div className="label-eyebrow text-[var(--brand-primary)] mb-3">Password reset requests · {resets.length} pending</div>
          <div className="space-y-2">
            {resets.map(r => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 border hairline p-3" data-testid={`reset-req-${r.id}`}>
                <div>
                  <div className="text-sm font-medium">{r.name || r.email}</div>
                  <div className="font-mono text-xs text-neutral-500">{r.email} · {(r.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="flex gap-2">
                  <button className="btn-primary text-xs" onClick={() => approve(r)} data-testid={`reset-approve-${r.id}`}>Approve & send temp password</button>
                  <button className={btn} onClick={() => deny(r)} data-testid={`reset-deny-${r.id}`}>Deny</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tempResult && (
        <div className="border-2 border-[color:var(--brand-primary)] bg-[color:var(--brand-surface2)] p-5" data-testid="temp-password-result">
          <div className="label-eyebrow text-[var(--brand-primary)] mb-2">Temporary password for {tempResult.email}</div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-xl tracking-wider bg-white border hairline px-4 py-2" data-testid="temp-password-value">{tempResult.temp_password}</span>
            <button className="btn-ghost text-xs" onClick={() => { navigator.clipboard?.writeText(tempResult.temp_password); toast.success("Copied"); }} data-testid="temp-password-copy">Copy</button>
          </div>
          <p className="text-xs text-neutral-600 mt-3">
            {tempResult.emailed
              ? "Also emailed to the user. They must set a new password on first sign-in."
              : "Email could not be delivered (domain not yet verified) — share this password with the user manually. They must set a new password on first sign-in."}
          </p>
          <button className="btn-ghost text-xs mt-3" onClick={() => setTempResult(null)} data-testid="temp-password-dismiss">Dismiss</button>
        </div>
      )}

      <div className="border hairline">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--brand-surface2)]">
            <tr className="text-left label-eyebrow">
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Department</th>
              <th className="p-3">Position</th>
              <th className="p-3">Contact</th>
              <th className="p-3">Role</th>
              <th className="p-3">Status</th>
              <th className="p-3">Joined</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const status = u.status || "active";
              return (
                <tr key={u.id} className={`border-t hairline ${status !== "active" ? "opacity-60" : ""}`} data-testid={`user-row-${u.id}`}>
                  <td className="p-3">{u.name}</td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3 text-neutral-600" data-testid={`user-department-${u.id}`}>{u.department || "—"}</td>
                  <td className="p-3 text-neutral-600" data-testid={`user-position-${u.id}`}>{u.position || "—"}</td>
                  <td className="p-3 font-mono text-xs text-neutral-600" data-testid={`user-contact-${u.id}`}>{u.contact || "—"}</td>
                  <td className="p-3"><span className="label-eyebrow text-[var(--brand-primary)]">{u.role}</span></td>
                  <td className="p-3">
                    <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 ${STATUS_STYLES[status]}`} data-testid={`user-status-${u.id}`}>{status}</span>
                    {u.email_muted && (
                      <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 ml-1 bg-neutral-200 text-neutral-600" data-testid={`user-muted-badge-${u.id}`}>muted</span>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs text-neutral-500">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="p-3">
                    {canManage(u) ? (
                      <div className="flex flex-wrap gap-1">
                        <button className={btn} onClick={() => setFormUser(u)} data-testid={`user-edit-${u.id}`}>Edit</button>
                        <button className={btn} onClick={() => toggleMute(u)} data-testid={`user-mute-${u.id}`}>
                          {u.email_muted ? "Unmute emails" : "Mute emails"}
                        </button>
                        {status === "active" ? (
                          <>
                            <button className={btn} onClick={() => setStatus(u, "inactive")} data-testid={`user-inactivate-${u.id}`}>Inactivate</button>
                            <button className={btn} onClick={() => setStatus(u, "cancelled")} data-testid={`user-cancel-${u.id}`}>Cancel</button>
                          </>
                        ) : (
                          <button className={btn} onClick={() => setStatus(u, "active")} data-testid={`user-reactivate-${u.id}`}>Reactivate</button>
                        )}
                        <button className="text-xs px-2 py-1 text-[#8B2E2E] hover:bg-[#8B2E2E]/10 transition-colors" onClick={() => del(u)} data-testid={`user-delete-${u.id}`}>Delete</button>
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
